import React, { useState, useEffect } from 'react'
import { 
  Tv, 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Star, 
  Hash, 
  TrendingUp,
  Monitor,
  Plus
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import channelService from '../../utils/services/ChannelService'
import deviceService from '../../utils/services/DeviceService'
import { useAuth } from '../../contexts/AuthContext'
import { useWatch } from '../../contexts/WatchContext'
import AttachChannelModal from './AttachChannelModal'

const UserChannels = () => {
  const { currentUserData } = useAuth()
  const { watchedUser } = useWatch()
  const [channels, setChannels] = useState([])
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [showAttachModal, setShowAttachModal] = useState(false)

  // Use watched user if available, otherwise use current user
  const targetUser = watchedUser || currentUserData



  // Load channels and devices on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('🔄 Fetching channels and devices for user:', watchedUser?.uid)
        setLoading(true)
        
        // Fetch active channels and devices in parallel
        const [channelsData, devicesData] = await Promise.all([
          channelService.getActiveChannels(),
          deviceService.getUserDevices(targetUser?.uid)
        ])
        
        console.log('✅ Fetched data:', { channels: channelsData.length, devices: devicesData.length })
        setChannels(channelsData)
        setDevices(devicesData)
      } catch (error) {
        console.error('❌ Error fetching data:', error)
        toast.error('Failed to load channels and devices')
      } finally {
        setLoading(false)
      }
    }

    console.log('👤 Target user:', targetUser)
    if (targetUser?.uid) {
      fetchData()
    } else {
      console.log('⏳ Waiting for user data...')
      setLoading(false) // Don't show loading if no user
    }
  }, [targetUser?.uid])

  const filteredChannels = channels.filter(channel => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      return channel.name?.toLowerCase().includes(searchLower) ||
             channel.description?.toLowerCase().includes(searchLower)
    }
    return true
  })

  const handleChannelClick = (channel) => {
    setSelectedChannel(channel)
    setShowAttachModal(true)
  }

  const handleAttachChannel = async (deviceId) => {
    try {
      console.log(`🔄 Attaching channel ${selectedChannel.name} to device ${deviceId}`)
      
      // Check if device already has a channel assigned
      const device = devices.find(d => d.key === deviceId)
      const hasExistingChannel = device?.channelId && device.channelId !== ''
      
      if (hasExistingChannel) {
        const existingChannel = channels.find(c => c.key === device.channelId)
        const existingChannelName = existingChannel?.name || 'Unknown Channel'
        console.log(`🔄 Replacing existing channel "${existingChannelName}" with "${selectedChannel.name}"`)
      }
      
      // Assign device to channel (this will clear screenId and replace existing channel)
      const updatedDevice = await deviceService.assignChannelToDevice(deviceId, selectedChannel.key)
      
      console.log('✅ Device assigned to channel:', updatedDevice)
      
      if (hasExistingChannel) {
        toast.success(`Channel replaced: "${selectedChannel.name}" is now attached to device`)
      } else {
        toast.success(`Channel "${selectedChannel.name}" attached to device successfully!`)
      }
      
      setShowAttachModal(false)
      setSelectedChannel(null)
      
      // Refresh devices list to show updated assignments
      const updatedDevices = await deviceService.getUserDevices(targetUser.uid)
      setDevices(updatedDevices)
    } catch (error) {
      console.error('❌ Error attaching channel:', error)
      toast.error('Failed to attach channel to device')
    }
  }



  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading channels and devices...</p>
        </div>
      </div>
    )
  }

  if (!targetUser?.uid) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Tv className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No User Data</h3>
          <p className="text-gray-600">Unable to load user information. Please try refreshing the page.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">All Channels</h1>
        <p className="text-gray-600">Browse all available channels and attach them to your devices</p>
      </div>

      {/* Legal Disclaimer */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Legal Disclaimer</h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p>
            This system provides links to publicly available video streams. No video files are hosted or stored. To the best of our knowledge, all links point to content intentionally made public by the copyright holders.
          </p>
          <p>
            If you believe any link infringes your rights, please submit a removal request. Note: removing a link here does not remove the content from the web — contact the actual hosting provider to request takedown. This system and its maintainers do not control the linked content.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search channels..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <Tv className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Tv className="w-8 h-8 text-primary-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Total Channels</p>
              <p className="text-2xl font-bold text-gray-900">{channels.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Play className="w-8 h-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Assigned Channels</p>
              <p className="text-2xl font-bold text-gray-900">
                {devices.filter(d => d.channelId && d.channelId !== '').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Monitor className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Devices Running Channels</p>
              <p className="text-2xl font-bold text-gray-900">
                {devices.filter(d => d.channelId && d.channelId !== '').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Channels Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredChannels.map((channel) => (
          <ChannelCard 
            key={channel.key} 
            channel={channel} 
            channels={channels}
            setChannels={setChannels}
            devices={devices}
            onClick={() => handleChannelClick(channel)}
          />
        ))}
      </div>

      {/* Empty State */}
      {filteredChannels.length === 0 && (
        <div className="text-center py-12">
          <Tv className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No channels found</h3>
          <p className="text-gray-600">
            {searchTerm ? 'Try adjusting your search terms.' : 'No channels are available at the moment.'}
          </p>
        </div>
      )}

      {/* Attach Channel Modal */}
      {showAttachModal && selectedChannel && (
        <AttachChannelModal
          channel={selectedChannel}
          devices={devices}
          onAttach={handleAttachChannel}
          onClose={() => {
            setShowAttachModal(false)
            setSelectedChannel(null)
          }}
        />
      )}
    </div>
  )
}

function ChannelCard({ channel, channels, setChannels, devices, onClick }) {
  // Find which device this channel is assigned to
  const assignedDevice = devices.find(device => device.channelId === channel.key)
  
  const getStatusIcon = (channel) => {
    if (!channel.isActive) return <Pause className="w-4 h-4 text-gray-400" />
    return channel.isMuted ? 
      <VolumeX className="w-4 h-4 text-orange-500" /> : 
      <Volume2 className="w-4 h-4 text-green-500" />
  }

  const getStatusText = (channel) => {
    if (!channel.isActive) return 'Inactive'
    return channel.isMuted ? 'Muted' : 'Live'
  }

  const getStatusColor = (channel) => {
    if (!channel.isActive) return 'text-gray-500 bg-gray-100'
    return channel.isMuted ? 'text-orange-600 bg-orange-100' : 'text-green-600 bg-green-100'
  }

  const handleMuteToggle = async (e) => {
    e.stopPropagation() // Prevent card click
    
    try {
      console.log(`🔄 Toggling mute for channel: ${channel.name} (currently ${channel.isMuted ? 'muted' : 'unmuted'})`)
      
      // Toggle the mute status
      const newMuteStatus = !channel.isMuted
      await channelService.toggleChannelMute(channel.key, newMuteStatus)
      
      // Update the local state
      const updatedChannels = channels.map(c => 
        c.key === channel.key 
          ? { ...c, isMuted: newMuteStatus, modified: new Date().getTime() }
          : c
      )
      setChannels(updatedChannels)
      
      // Show success message
      toast.success(`Channel "${channel.name}" ${newMuteStatus ? 'muted' : 'unmuted'} successfully!`)
      
      console.log(`✅ Channel mute toggled successfully: ${channel.name} is now ${newMuteStatus ? 'muted' : 'unmuted'}`)
    } catch (error) {
      console.error('❌ Error toggling channel mute:', error)
      toast.error(`Failed to ${channel.isMuted ? 'unmute' : 'mute'} channel`)
    }
  }

  return (
    <div 
      className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 hover:border-primary-300 cursor-pointer group"
      onClick={onClick}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-gray-100 rounded-t-xl overflow-hidden">
        {channel.thumbnail ? (
          <img
            src={channel.thumbnail}
            alt={channel.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <Tv className="w-12 h-12 text-gray-400" />
          </div>
        )}
        
        {/* Status indicators - stacked vertically on the left */}
        <div className="absolute top-3 left-3 flex flex-col space-y-1">
          {/* Premium badge */}
          {channel.isPremium && (
            <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium text-yellow-800 bg-yellow-100 border border-yellow-200">
              <Star className="w-3 h-3 mr-1" />
              Premium
            </div>
          )}
        </div>
        
        {/* Mute button - top right */}
        {channel.isActive && (
          <div className="absolute top-3 right-3">
            <button
              onClick={handleMuteToggle}
              className={`p-2 rounded-full transition-colors ${
                channel.isMuted 
                  ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                  : 'bg-black bg-opacity-50 hover:bg-opacity-70 text-white'
              }`}
              title={channel.isMuted ? 'Unmute Channel' : 'Mute Channel'}
            >
              {channel.isMuted ? (
                <VolumeX className="w-4 h-4" />
              ) : (
                <Volume2 className="w-4 h-4" />
              )}
            </button>
          </div>
        )}
        
        {/* Status indicator - bottom left */}
        <div className="absolute bottom-3 left-3">
          <div className={`p-1.5 rounded-full ${channel.isActive ? 'bg-green-500' : 'bg-gray-400'}`}>
            <div className="w-2 h-2 rounded-full bg-white"></div>
          </div>
        </div>
        
        {/* Device Assignment Status */}
        <div className="absolute bottom-3 right-3">
          {assignedDevice ? (
            <div className="px-3 py-1.5 rounded-full bg-green-600 text-white text-xs font-medium shadow-sm flex items-center">
              <Monitor className="w-3 h-3 mr-1" />
              {assignedDevice.name || 'Device'}
            </div>
          ) : (
            <div className="px-3 py-1.5 rounded-full bg-primary-600 text-white text-xs font-medium group-hover:bg-primary-700 transition-colors shadow-sm">
              Assign to Device
            </div>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center">
            <Hash className="w-4 h-4 text-gray-400 mr-1" />
            <span className="text-sm font-medium text-gray-500">#{channel.number}</span>
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <TrendingUp className="w-4 h-4 mr-1" />
            {channel.hits || 0} views
          </div>
        </div>
        
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-1">{channel.name}</h3>
        
        {channel.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{channel.description}</p>
        )}
        
        <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-100">
          <span>Created: {new Date(channel.created).toLocaleDateString()}</span>
          <span>Modified: {new Date(channel.modified).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  )
}

export default UserChannels 